/**
 * All Categories Page
 * Displays all category cards without filtering
 */

import React, { useState, useEffect } from 'react';
import SEO from '../components/Common/SEO';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { categoryService, Category } from '../services/categoryService';
import { tourService } from '../services/tourService';
import { useTheme } from '../contexts/ThemeContext';
import { useCategoryOrder } from '../contexts/CategoryOrderContext';
import { applyFeaturedOrderWithCategory } from '../utils/applyFeaturedOrder';
import { getCategorySubtitle } from '../utils/categoryTitles';
import FloatingFilterWrapper from '../components/Common/FloatingFilterWrapper';
import { Tour } from '../types';

const AllCategories: React.FC = () => {

  const { actualTheme } = useTheme();
  const { featuredCategories: categories, loading: categoriesLoading } = useCategoryOrder();
  const [categoryImages, setCategoryImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (categories.length > 0) {
      if (Object.keys(categoryImages).length === 0) {
        loadCategoryImages(categories);
      }
    }
  }, [categories, categoryImages]);

  // Reload images whenever tours are saved/reassigned
  useEffect(() => {
    const refresh = () => {
      setCategoryImages({});
      if (categories.length > 0) loadCategoryImages(categories);
    };
    window.addEventListener('toursUpdated', refresh);
    window.addEventListener('categoryToursUpdated', refresh);
    return () => {
      window.removeEventListener('toursUpdated', refresh);
      window.removeEventListener('categoryToursUpdated', refresh);
    };
  }, [categories]);

  // Update loading state
  useEffect(() => {
    setLoading(categoriesLoading);
  }, [categoriesLoading]);

  const loadCategoryImages = async (categories: Category[]) => {
    const images: Record<string, string> = {};

    try {
      console.log('🖼️ AllCategories: Loading category images...');
      const allTours = await tourService.getAllTours(true);
      console.log(`📦 AllCategories: Loaded ${allTours.length} tours`);

      for (const category of categories) {
        let categoryTours = allTours.filter((tour: Tour) => {
          const tourCategoryId = (tour as any).categoryId;
          // Prefer categoryId; fall back to name only when categoryId is absent
          if (tourCategoryId) return tourCategoryId === category.id;
          return tour.category?.toLowerCase() === category.name.toLowerCase();
        });

        categoryTours = applyFeaturedOrderWithCategory(categoryTours, category.id);

        if (categoryTours.length > 0) {
          const firstTour = categoryTours[0];
          if (firstTour.images && firstTour.images.length > 0) {
            images[category.id] = firstTour.images[0];
          }
        }
      }
      setCategoryImages(images);
    } catch (error) {
      console.error('❌ AllCategories: Error loading category images:', error);
    }
  };

  const getCategoryImage = (category: Category): string => {
    if (categoryImages[category.id]) {
      return categoryImages[category.id];
    }
    return '';
  };

  return (
    <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'}`}>
      <SEO
        title="Tour Categories"
        description="Discover our diverse range of safari categories, from luxury wildlife encounters to authentic cultural immersions."
        keywords={['safari categories', 'cultural tours', 'adventure safaris', 'photography safaris', 'wildlife experiences']}
      />
      {/* Header */}
      <div className={`pt-12 pb-6 sm:pt-20 sm:pb-10 relative z-20 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className={`
            rounded-[2.5rem] p-8 sm:p-12 text-center shadow-2xl relative overflow-hidden
            ${actualTheme === 'dark'
              ? 'bg-slate-800 border border-slate-700 shadow-slate-900/50'
              : 'bg-white border border-white/50 shadow-xl shadow-primary-900/5'
            }
          `}>
            {/* Decorative Background Elements */}
            <div className={`absolute top-0 left-0 w-32 h-32 rounded-full blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2 ${actualTheme === 'dark' ? 'bg-primary-500' : 'bg-primary-400'}`} />
            <div className={`absolute bottom-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-20 translate-x-1/3 translate-y-1/3 ${actualTheme === 'dark' ? 'bg-amber-500' : 'bg-amber-400'}`} />

            <h1 className={`relative text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight ${actualTheme === 'dark' ? 'text-slate-100' : 'text-gray-900'
              }`}>
              All Categories
            </h1>
            <p className={`relative text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
              }`}>
              Explore all our tour categories and discover amazing experiences
            </p>
          </div>
        </div>
      </div>

      {/* Floating Filter Wrapper */}
      <FloatingFilterWrapper />

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-10 lg:py-12 pb-24 md:pb-20">
        {loading ? (
          <div className="flex flex-col sm:flex-row justify-center items-center py-8 sm:py-12 gap-4">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary-800"></div>
            <span className={`text-sm sm:text-base transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
              }`}>Loading categories...</span>
          </div>
        ) : categories.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 relative" style={{ isolation: 'isolate', zIndex: 1 } as React.CSSProperties}>
            {categories.map((category: Category, index: number) => {
              // Validate category ID - ensure it exists and is not empty
              const categoryId = category?.id;

              // Reserved route names that should not be used as category IDs
              const reservedRoutes = ['about', 'categories', 'contact', 'tours', 'home', 'admin', 'login'];

              if (!categoryId ||
                typeof categoryId !== 'string' ||
                categoryId.trim() === '' ||
                categoryId === 'undefined' ||
                categoryId === 'null' ||
                reservedRoutes.includes(categoryId.toLowerCase())) {
                console.warn(`⚠️ Category "${category?.name || 'Unknown'}" has invalid or reserved ID:`, categoryId);
                return null;
              }

              // Ensure the route path is valid and properly encoded
              const categoryRoute = `/category/${encodeURIComponent(categoryId)}`;

              return (
                <Link
                  key={categoryId}
                  to={categoryRoute}
                  className={`group relative block rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500 border floating-card hover:floating-card-hover cursor-pointer ${actualTheme === 'dark'
                    ? 'bg-slate-800 border-slate-700/50'
                    : 'bg-white border-neutral-200/50'
                    }`}
                  style={{
                    // Use block display for the link wrapper
                    display: 'block',
                    zIndex: 10,
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation', // Key for smooth scrolling
                    isolation: 'isolate',
                  }}
                  data-category-id={categoryId}
                  data-category-name={category.name}
                >
                  {/* Category Image with Overlay */}
                  <div className="relative h-56 sm:h-64 md:h-72 overflow-hidden rounded-t-2xl sm:rounded-t-3xl pointer-events-none" style={{ pointerEvents: 'none' }}>
                    {/* Base Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-black/10 to-black/50 z-10 pointer-events-none" style={{ pointerEvents: 'none' }}></div>

                    {/* Image with Smooth Zoom */}
                    {getCategoryImage(category) ? (
                      <img
                        src={getCategoryImage(category)}
                        alt={category.name}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-115"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className={`w-full h-full bg-gradient-to-br flex items-center justify-center transition-colors duration-300 ${actualTheme === 'dark'
                        ? 'from-primary-800 to-primary-900'
                        : 'from-primary-200 to-primary-300'
                        }`}>
                        <span className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-primary-300' : 'text-primary-600'
                          }`}>No image available</span>
                      </div>
                    )}

                    {/* Animated Gradient Overlay on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20 pointer-events-none" style={{ pointerEvents: 'none' }}></div>

                    {/* Category Badge - Top Right with Theme Colors */}
                    <div className="absolute top-5 right-5 z-30 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 pointer-events-none" style={{ pointerEvents: 'none' }}>
                      <span className={`inline-flex items-center px-5 py-2.5 rounded-full text-sm font-bold text-white shadow-2xl backdrop-blur-lg border-2 border-white/40 transition-colors duration-300 ${actualTheme === 'dark'
                        ? 'bg-primary-700 hover:bg-primary-600'
                        : 'bg-primary-800 hover:bg-primary-700'
                        }`}>
                        {category.name}
                      </span>
                    </div>

                    {/* Category Color Accent - Bottom Left - Minimized */}
                    <div className="absolute bottom-5 left-5 z-30 transform transition-all duration-700 group-hover:scale-110 pointer-events-none" style={{ pointerEvents: 'none' }}>
                      <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg flex items-center justify-center">
                        <div className={`w-3 h-3 rounded-full transition-colors duration-300 ${actualTheme === 'dark' ? 'bg-primary-600' : 'bg-primary-800'
                          }`}></div>
                      </div>
                    </div>

                    {/* Shine Effect on Hover - Enhanced */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out z-30 pointer-events-none" style={{ pointerEvents: 'none' }}></div>

                    {/* Subtle Pulse Effect */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-15 pointer-events-none"
                      style={{
                        background: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.2) 100%)',
                        pointerEvents: 'none'
                      }}
                    ></div>
                  </div>

                  {/* Category Content */}
                  <div className={`p-5 sm:p-6 md:p-7 relative overflow-hidden transition-colors duration-300 pointer-events-none z-10 ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
                    }`}
                    style={{ pointerEvents: 'none' }}
                  >
                    {/* Subtle Background Pattern */}
                    <div
                      className="absolute top-0 right-0 w-40 h-40 opacity-5 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none z-0"
                      style={{
                        background: `radial-gradient(circle, ${category.color || '#1e3a8a'} 0%, transparent 70%)`,
                        pointerEvents: 'none'
                      }}
                    ></div>

                    <div className="relative z-10 mb-4 sm:mb-5 md:mb-6">
                      <h3 className={`text-xl sm:text-2xl font-bold mb-2 sm:mb-3 transition-colors duration-300 transform group-hover:translate-x-1 ${actualTheme === 'dark'
                        ? 'text-white group-hover:text-primary-400'
                        : 'text-primary-900 group-hover:text-primary-800'
                        }`}>
                        {category.name} Experiences
                      </h3>
                      <p className={`text-xs sm:text-sm leading-relaxed line-clamp-2 transition-colors duration-300 ${actualTheme === 'dark'
                        ? 'text-slate-300 group-hover:text-slate-200'
                        : 'text-neutral-600 group-hover:text-neutral-700'
                        }`}>
                        {category.description || getCategorySubtitle(category.name)}
                      </p>
                    </div>

                    {/* CTA with Enhanced Styling */}
                    <div className={`flex items-center justify-between pt-4 sm:pt-5 md:pt-6 border-t-2 relative z-10 transition-colors duration-300 pointer-events-none ${actualTheme === 'dark' ? 'border-slate-700/50' : 'border-neutral-200/50'
                      }`}
                      style={{ pointerEvents: 'none' }}
                    >
                      <span className={`text-xs sm:text-sm font-semibold transition-colors duration-300 pointer-events-none ${actualTheme === 'dark'
                        ? 'text-slate-300 group-hover:text-primary-400'
                        : 'text-neutral-700 group-hover:text-primary-800'
                        }`}>
                        Explore Tours
                      </span>
                      <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full text-white group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-xl group-hover:shadow-2xl relative overflow-hidden pointer-events-none ${actualTheme === 'dark'
                        ? 'bg-gradient-to-br from-primary-700 to-primary-800 group-hover:from-primary-600 group-hover:to-primary-700'
                        : 'bg-gradient-to-br from-primary-800 to-primary-900 group-hover:from-primary-700 group-hover:to-primary-800'
                        }`}>
                        {/* Button Shine Effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 pointer-events-none"></div>
                        <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  {/* Decorative Corner Accents */}
                  <div
                    className="absolute top-0 right-0 w-40 h-40 opacity-5 group-hover:opacity-15 transition-opacity duration-500 rounded-bl-full pointer-events-none z-0"
                    style={{
                      background: `radial-gradient(circle at top right, ${category.color || '#1e3a8a'} 0%, transparent 70%)`
                    }}
                  ></div>
                  <div
                    className="absolute bottom-0 left-0 w-32 h-32 opacity-5 group-hover:opacity-10 transition-opacity duration-500 rounded-tr-full pointer-events-none z-0"
                    style={{
                      background: `radial-gradient(circle at bottom left, ${category.color || '#1e3a8a'} 0%, transparent 70%)`
                    }}
                  ></div>
                </Link>
              );
            }).filter(Boolean)}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className={`text-lg transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
              }`}>
              No categories available at the moment. Please check back later.
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default AllCategories;
